/**
 * M6.5-W9 — ArchiMate-symbol SVG-library.
 *
 * Bulk-importeert de 43 SVG-bestanden in `./archimate-icons/*.svg` als
 * raw-string content via Vite's `?raw`-import. Elke SVG gebruikt
 * `stroke="currentColor"` zodat ConceptNode de kleur kan sturen via de
 * `color`-property op de wrapper.
 *
 * Mapping: ArchiMate-typeId → bestandsnaam (zonder .svg). Subset van
 * de types deelt dezelfde SVG (Application/Technology Collaboration
 * gebruiken `collaboration.svg`, etc.).
 */
import { getRegistry } from "../../registry";

const SVG_MODULES = import.meta.glob<string>("./archimate-icons/**/*.svg", {
  query: "?raw",
  import: "default",
  eager: true,
});

/** Stereotype-id → SVG-pad (relatief aan `./archimate-icons/`). Wint van
 *  de typeId-lookup — een Vulnerability-stereotype op een Assessment
 *  rendert dus de risk-Vulnerability-SVG, niet de Assessment-SVG. */
const STEREOTYPE_TO_SVG: Record<string, string> = {
  // Risk & Security
  "risk.Risk": "risk-security/risk",
  "risk.Vulnerability": "risk-security/vulnerability",
  "risk.ThreatEvent": "risk-security/threat",
  "risk.ThreatActor": "risk-security/threat-agent",
  "risk.Asset": "risk-security/asset-at-risk",
  "risk.LossEvent": "risk-security/loss-event",
  "risk.ControlObjective": "risk-security/control-objective",
  "risk.Countermeasure": "risk-security/control-measure",
  "risk.SecurityPrinciple": "risk-security/security-principle",

  // Sharp Concept Modelling
  "sharp.Concept": "sharp-concept/concept",
  "sharp.SubjectArea": "sharp-concept/subtype",
  // Roadmap-element komt straks vanuit Sharp/Roadmap-extensie (M7+);
  // mapping vooraf gezet zodat 't direct werkt zodra 't pack 'm
  // toevoegt onder `sharp.Roadmap`.
  "sharp.Roadmap": "sharp-concept/roadmap",

  // C4 — base stereotypes
  "c4.Container": "c4/container",
  "c4.Component": "c4/component",
  "c4.ContainerInstance": "c4/container-instance",
  "c4.SystemContext": "c4/software-system",
  "c4.ExternalSystem": "c4/software-system-external",
  "c4.Person": "c4/person",
  "c4.SystemBoundary": "c4/boundary",
  "c4.ContainerBoundary": "c4/boundary",
  "c4.EnterpriseBoundary": "c4/boundary",
  // C4 — Container sub-stereotypes (specifieke runtime-vormen)
  "c4.Database": "c4/database",
  "c4.MessageBroker": "c4/message-broker",
  "c4.Microservice": "c4/microservice",
  "c4.Browser": "c4/browser",
  "c4.MobileDevice": "c4/mobile-device",

  // Sharp (concept-level stereotypes)
  "sharp.ConceptConstraint": "sharp-concept/concept-constraint",

  // ArchiMate 3.1-compat (fase 2 van de ArchiMate 4-migratie). Alleen de
  // vier gevallen waar het merge-doel een ANDER icoon heeft dan het oude
  // type — zonder deze override zou een gemigreerd «Gap» het
  // Assessment-vergrootglas krijgen. De overige veertien compat-
  // stereotypes (Business/Application/Technology Process, Function,
  // Service, Event, Collaboration, Role) landen op een generiek type dat
  // via TYPE_TO_SVG al het juiste laag-onafhankelijke icoon levert.
  "archimate31.Constraint": "constraint",
  "archimate31.Contract": "contract",
  "archimate31.Gap": "gap",
  "archimate31.Representation": "representation",
};

const TYPE_TO_SVG: Record<string, string> = {
  // Business
  "archimate.BusinessActor": "actor",
  "archimate.BusinessRole": "role",
  "archimate.BusinessCollaboration": "collaboration",
  "archimate.BusinessInterface": "interface",
  "archimate.BusinessProcess": "process",
  "archimate.BusinessFunction": "function",
  "archimate.BusinessInteraction": "interaction",
  "archimate.BusinessEvent": "event",
  "archimate.BusinessService": "service",
  "archimate.BusinessObject": "object",
  "archimate.Contract": "contract",
  "archimate.Representation": "representation",
  "archimate.Product": "product",

  // Application
  "archimate.ApplicationComponent": "component",
  "archimate.ApplicationCollaboration": "collaboration",
  "archimate.ApplicationInterface": "interface",
  "archimate.ApplicationFunction": "function",
  "archimate.ApplicationInteraction": "interaction",
  "archimate.ApplicationProcess": "process",
  "archimate.ApplicationEvent": "event",
  "archimate.ApplicationService": "service",
  "archimate.DataObject": "object",

  // Technology
  "archimate.Node": "node",
  "archimate.Device": "device",
  "archimate.SystemSoftware": "system-software",
  "archimate.TechnologyCollaboration": "collaboration",
  "archimate.TechnologyInterface": "interface",
  "archimate.Path": "path",
  "archimate.CommunicationNetwork": "communication-network",
  "archimate.TechnologyProcess": "process",
  "archimate.TechnologyInteraction": "interaction",
  "archimate.TechnologyEvent": "event",
  "archimate.TechnologyService": "service",
  "archimate.Artifact": "artifact",

  // Physical
  "archimate.Equipment": "equipment",
  "archimate.Facility": "facility",
  "archimate.DistributionNetwork": "distribution-network",
  "archimate.Material": "material",

  // Strategy
  "archimate.Resource": "resource",
  "archimate.Capability": "capability",
  "archimate.ValueStream": "value-stream",
  "archimate.CourseOfAction": "course-of-action",

  // Motivation
  "archimate.Stakeholder": "stakeholder",
  "archimate.Driver": "driver",
  "archimate.Assessment": "assessment",
  "archimate.Goal": "goal",
  "archimate.Outcome": "outcome",
  "archimate.Principle": "principle",
  "archimate.Requirement": "requirement",
  "archimate.Constraint": "constraint",
  "archimate.Meaning": "meaning",
  "archimate.Value": "value",

  // Implementation & Migration
  "archimate.WorkPackage": "work-package",
  "archimate.Deliverable": "deliverable",
  "archimate.ImplementationEvent": "event",
  "archimate.Plateau": "plateau",
  "archimate.Gap": "gap",
  "archimate.Program": "archimate-ext/program",

  // Extension-types (niet in de spec maar wel in onze packs)
  "archimate.Metric": "archimate-ext/metric",
  "archimate.Guideline": "archimate-ext/guideline",
  "archimate.UseCase": "archimate-ext/usecase",

  // Generic
  "archimate.Location": "location",
  "archimate.Grouping": "grouping",
  // SecurityDomain is een extension-type in onze archimate-pack;
  // hergebruikt de risk-security/security-domain.svg.
  "archimate.SecurityDomain": "risk-security/security-domain",

  // Sharp (concept-level, not stereotype-overlay)
  "sharp.Attribute": "sharp-concept/concept-attribute",
  "sharp.Identifier": "sharp-concept/identifier",

  // ArchiMate 4 Common Domain — de generieke types waarin de laag-
  // gebonden 3.1-varianten samenvallen. De icoon-bibliotheek is al
  // laag-onafhankelijk gekeyed (`process`/`function`/... zonder
  // laag-prefix), dus dit zijn hergebruikte bestanden, geen nieuwe SVG's.
  "archimate.Process": "process",
  "archimate.Function": "function",
  "archimate.Service": "service",
  "archimate.Event": "event",
  "archimate.Collaboration": "collaboration",
  "archimate.Role": "role",
};

/** Returns the raw SVG content for an ArchiMate type, taking stereotypes
 *  into account. Stereotype-mappings winnen — een Assessment met
 *  `risk.Vulnerability`-stereotype krijgt vulnerability.svg, niet
 *  assessment.svg. Null wanneer geen mapping bestaat (bv. Grouping). */
/** Ruwe SVG-inhoud op naam, bv. `risk-security/risk`. De namen die hier
 *  geldig zijn, zijn precies wat `listIconNames()` teruggeeft. */
export function getSvgByName(name: string): string | null {
  return SVG_MODULES[`./archimate-icons/${name}.svg`] ?? null;
}

/** Alle beschikbare icoonnamen, gesorteerd — de keuzelijst voor een profiel. */
export function listIconNames(): string[] {
  return Object.keys(SVG_MODULES)
    .map((path) => path.replace("./archimate-icons/", "").replace(".svg", ""))
    .sort();
}

export function getArchimateSvgContent(
  typeId: string,
  stereotypes?: readonly string[],
): string | null {
  if (stereotypes && stereotypes.length > 0) {
    for (const st of stereotypes) {
      // Eerst de hardcoded map (pack-profielen), dan het `icon_id`-veld op de
      // definitie. Die tweede tak is wat een zelf gedefinieerd profiel een
      // eigen symbool geeft: dat staat in D1 en kan dus niet in een map staan
      // die bij het bouwen wordt vastgelegd.
      const stPath = STEREOTYPE_TO_SVG[st] ?? getRegistry().getStereotype(st)?.icon_id;
      if (stPath) {
        const content = SVG_MODULES[`./archimate-icons/${stPath}.svg`];
        if (content) return content;
      }
    }
  }
  const name = TYPE_TO_SVG[typeId];
  if (!name) return null;
  const content = SVG_MODULES[`./archimate-icons/${name}.svg`];
  return content ?? null;
}

/** True wanneer een type (of een van z'n stereotypes) een SVG-icon
 *  heeft = renderbaar in ArchiMate-symbol-modus. */
export function hasArchimateSvgIcon(typeId: string, stereotypes?: readonly string[]): boolean {
  return getArchimateSvgContent(typeId, stereotypes) !== null;
}
